import {Log} from '@aboutyou/mlm';
import type {RequestHandler} from 'express';
import {parseRequestProperties} from './parseRequestProperties';
import {createStaticConfig} from './staticConfig/createStaticConfig';
import {ChunkExtractor} from './utils/ChunkExtractor';
import {generateBaseStyles} from './utils/generateBaseStyles';
import {CookieConfig} from './utils/parseRequestCookies';
import {generateStyles} from './utils/serverCreateStartOfDocumentWriter';
import {setAppVersionHeader} from './utils/setAppVersion';

export const CACHEABLE_CRITICAL_CSS_PLACEHOLDER = '###CRITICAL_CSS###';

interface FactoryConfigReactMiddlewareStyles {
  htmlTemplate?: string;
  templateAppCheckout?: string;
  statsFile?: string;
  publicDir: string;
  cookieConfig?: CookieConfig;
  stack: 'mobile' | 'desktop';
}

const createReactServerMiddlewareStyles = (
  config: FactoryConfigReactMiddlewareStyles,
): RequestHandler => {
  return async function reactMiddleware(req, res) {
    try {
      Log.transact('GET /__/cacheable_styles', {
        timestamp: String(Date.now()),
      });

      const {isAppCheckout, abTestGroups, traceId} = parseRequestProperties({
        req,
        cookieConfig: config.cookieConfig,
      });

      const {hostConfig} = await createStaticConfig({
        traceId,
        originalRequest: req,
        hostname: req.hostname,
        abTestGroups,
        enableRealUserMode: false,
      });

      const chunkExtractor = new ChunkExtractor({
        publicPath: hostConfig.assetsUrl || '/',
        htmlTemplate: isAppCheckout
          ? config.templateAppCheckout || config.htmlTemplate
          : config.htmlTemplate,
        statsFile: config.statsFile,
        publicDir: config.publicDir,
      });

      const linariaCriticalStyles = await generateStyles(chunkExtractor);
      const styledComponentsCriticalCss = generateBaseStyles({
        locale: hostConfig.locale,
      });

      setAppVersionHeader(res);

      return res.json({
        html:
          linariaCriticalStyles.inlineStyles +
          linariaCriticalStyles.styleTags +
          styledComponentsCriticalCss,
        additionalHtmlResourceTags: '',
      });
    } catch (error) {
      Log.error('@aboutyou/bootstrap', 'Cacheable Styles rendering error', {
        error,
      });

      res.status(500);
      return res.json({error: error.message});
    }
  };
};

export {createReactServerMiddlewareStyles};
